// react
import React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { useTheme } from '@mui/material/styles';

// shared components
import HtmlTooltip from '@/components/ui/tooltip/HtmlTooltip';

// feature-utils
import {
  getBillingMeterHealthStatus,
  getBuildingSortedByEnergySavings,
  getEnergySavings,
  getJobSubscriptionData,
  getJobSubscriptionDataFiltered,
  getMaxLatencyConsumptionHours,
  getMaxLatencyHours,
  getPreviousEnergySavings,
  getQualityOfService,
} from '@/features/building-optimization/utils/utilityFunctions';

function parseAndFormatErrorMessage(errorMessage) {
  // Regular expression to match ISO 8601 timestamp format
  const timestampRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+\d{4}/g;

  // Function to format date strings
  function formatDate(dateString) {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  // Find all matches in the error message
  const timestamps = errorMessage.match(timestampRegex);

  // Check if exactly two timestamps were found
  if (timestamps && timestamps.length === 2) {
    // Extract and format dates from timestamps
    const startDate = formatDate(timestamps[0].split('T')[0]);
    const endDate = formatDate(timestamps[1].split('T')[0]);

    // Replace the original timestamps in the error message with the formatted dates
    const formattedErrorMessage = errorMessage
      .replace(timestamps[0], startDate)
      .replace(timestamps[1], endDate);

    return formattedErrorMessage;
  } else {
    // Return the original message if the expected format isn't found
    return errorMessage;
  }
}

const OperationsCard = ({
  groupKey,
  accounts,
  onCardDoubleClick,
  activeFilter,
}) => {
  const { t } = useTranslation('translation');
  const theme = useTheme();
  const [open] = React.useState(false);

  const descriptionElementRef = React.useRef(null);
  React.useEffect(() => {
    if (open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open]);

  // Aggregating data across all accounts
  const buildings = accounts.flatMap((account) => account.Buildings);
  const energySavings = buildings
    .map((building) => getEnergySavings(building))
    .filter((es) => es !== null);
  const buildingsSortedByEnergySavings =
    getBuildingSortedByEnergySavings(buildings);
  const minEnergySavings = Math.min(...energySavings);
  const maxEnergySavings = Math.max(...energySavings);

  const qos = buildings.map((building) =>
    getQualityOfService(
      getJobSubscriptionData(building, 'PredictiveHeatControl'),
    ),
  );
  const minQos = Math.min(...qos);
  const maxQos = Math.max(...qos);

  const gatewayHealthStatuses = buildings.flatMap((building) => {
    const subscriptions = getJobSubscriptionDataFiltered(
      building,
      'PredictiveHeatControl',
    );

    // Conditionally apply the active filter based on activeFilter prop
    const filteredSubscriptions = activeFilter
      ? subscriptions?.filter(
          (subscription) => subscription?.StateCode === 'Active',
        )
      : subscriptions; // If activeFilter is false, use all subscriptions

    return filteredSubscriptions?.map((subscription) => {
      let uptimePercentage = 0; // Default to 0 if no valid data is found

      if (
        subscription?.State?.GatewayHealthCheck?.Data?.UptimePercentage !==
        undefined
      ) {
        uptimePercentage = Math.round(
          subscription.State.GatewayHealthCheck.Data.UptimePercentage,
        );
      }

      if (uptimePercentage === 0) {
        console.warn(
          `Uptime is 0 or missing for building: ${
            building.Name
          }, subscription: ${subscription?.DisplayName || 'Unknown'}`,
        );
      }

      return {
        subscriptionId: subscription?.JobSubscriptionId,
        displayName: subscription?.DisplayName || 'Unknown',
        gatewayId: subscription?.UserInput?.GatewayId,
        uptimePercentage,
      };
    });
  });

  // Sort the gatewayHealthStatuses by uptimePercentage (ascending order)
  const sortedGatewayHealthStatuses = gatewayHealthStatuses.sort(
    (a, b) => a.uptimePercentage - b.uptimePercentage,
  );

  const sortedBuildings = buildings.sort((a, b) => {
    // Get latency hours for each building
    const latencyA = getMaxLatencyHours(a);
    const latencyB = getMaxLatencyHours(b);

    // Get critical errors for each building
    const hasCriticalErrorsA =
      getBillingMeterHealthStatus(a.State)?.critical_errors?.length > 0;
    const hasCriticalErrorsB =
      getBillingMeterHealthStatus(b.State)?.critical_errors?.length > 0;

    // Sort logic:
    // 1. Buildings with critical errors at the top
    if (hasCriticalErrorsA && !hasCriticalErrorsB) return -1;
    if (!hasCriticalErrorsA && hasCriticalErrorsB) return 1;

    // 2. Buildings with null latency at the top
    if (latencyA === null && latencyB !== null) return -1;
    if (latencyA !== null && latencyB === null) return 1;

    // 3. Sort by latency hours in descending order (highest latency on top)
    if (latencyA !== null && latencyB !== null) return latencyB - latencyA;

    return 0; // No sorting if both are equal in terms of critical errors and latency
  });

  // Extract uptime percentages to calculate the minimum
  const uptimePercentages = gatewayHealthStatuses.map(
    (status) => status.uptimePercentage,
  );
  const minUptimePercentage = Math.min(...uptimePercentages);

  // Aggregating MaxLatencyHours for BillingMeterHealthCheck
  const maxLatencyHoursList = buildings
    .map((building) => getMaxLatencyHours(building))
    .filter((latency) => latency !== null);
  const criticalErrors = buildings
    ?.map(
      (building) =>
        getBillingMeterHealthStatus(building.State)?.critical_errors,
    )
    .flat();
  const minLatencyHours = Math.min(...maxLatencyHoursList);
  const maxLatencyHours = Math.max(...maxLatencyHoursList);

  const maxLatencyConsumptionList = buildings
    .map((building) => getMaxLatencyConsumptionHours(building))
    .filter((latency) => latency !== null);
  const minLatencyConsumptionHours = Math.min(...maxLatencyConsumptionList);
  const maxLatencyConsumptionHours = Math.max(...maxLatencyConsumptionList);

  const getProgressColor = (value) => {
    if (value >= 90)
      return theme.palette.mode === 'light' ? 'success.light' : 'success.dark';
    if (value >= 40)
      return theme.palette.mode === 'light' ? 'warning.light' : 'warning.dark';
    return theme.palette.mode === 'light' ? 'error.light' : 'error.dark';
  };

  const getLatencyColor = (hours) => {
    if (hours === null) {
      return theme.palette.mode === 'light'
        ? theme.palette.text.primary
        : theme.palette.text.secondary;
    }
    if (hours < 168) return theme.palette.success.main; // less than 1 week
    if (hours < 336) return theme.palette.warning.main; // between 1 and 2 weeks
    return theme.palette.error.main; // more than 2 weeks
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              {groupKey}
            </Typography>
            <Tooltip
              title={t('buildingOptimization:tooltips.drillDown')}
              placement="top"
            >
              <IconButton sx={{ borderRadius: 1 }} onClick={onCardDoubleClick}>
                <LaunchRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider sx={{ mt: 1, mb: 1 }} />
          <Stack>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">
                  {t('translation:savings')}
                </Typography>
                <HtmlTooltip
                  title={t('buildingOptimization:tooltips.savingsPercentage')}
                  content={buildingsSortedByEnergySavings.map(
                    (building, index) => {
                      const savings = getEnergySavings(building); // Safely access savings
                      const previousSavings =
                        getPreviousEnergySavings(building); // Safely access previous savings
                      return (
                        <ListItemText
                          key={index}
                          primary={
                            <Chip
                              icon={<ApartmentRoundedIcon />}
                              size="small"
                              sx={{ borderRadius: 1, mr: 1, mb: 0.5 }}
                              label={<>{building.Name}</>}
                            />
                          }
                          secondary={
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Box>
                                <ListItemText
                                  primary={
                                    previousSavings.length > 0
                                      ? previousSavings[0]?.label
                                      : 'N/A'
                                  }
                                  secondary={
                                    previousSavings.length > 0
                                      ? previousSavings[0]?.value.toFixed(1) +
                                        '%'
                                      : 'No data'
                                  }
                                  slotProps={{
                                    primary: {
                                      fontSize: '0.8rem',
                                      color: theme.palette.text.secondary,
                                    }
                                  }}
                                />
                              </Box>
                              <Box>
                                <ListItemText
                                  primary={savings ? 'Indeværende' : 'N/A'}
                                  secondary={
                                    savings
                                      ? savings?.toFixed(1) + '%'
                                      : 'No data'
                                  }
                                  slotProps={{
                                    primary: {
                                      fontSize: '0.8rem',
                                      color: theme.palette.text.secondary,
                                    }
                                  }}
                                />
                              </Box>
                            </Box>
                            // typeof savings === 'number' ? `${savings.toFixed(1)}%` : 'N/A'
                          }
                          slotProps={{
                            secondary: {
                              color:
                                savings < 7.5
                                  ? 'error.main'
                                  : savings <= 18
                                    ? 'warning.main'
                                    : 'success.main',
                            }
                          }}
                        />
                      );
                    },
                  )}
                >
                  <InfoRoundedIcon
                    fontSize="small"
                    sx={{ ml: 0.5, color: 'divider' }}
                  />
                </HtmlTooltip>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'end',
                }}
              >
                {energySavings ? (
                  <ListItemText
                    primary="Indeværende"
                    secondary={
                      <>
                        <Typography
                          component="span"
                          sx={{
                            color:
                              minEnergySavings < 7.5
                                ? 'error.main'
                                : minEnergySavings <= 18
                                  ? 'warning.main'
                                  : 'success.main',
                          }}
                        >
                          {minEnergySavings.toFixed(1)}%
                        </Typography>
                        {minEnergySavings !== maxEnergySavings && (
                          <>
                            <Typography
                              component="span"
                              sx={{ color: 'text.primary', mx: 0.5 }}
                            >
                              {' '}
                              -{' '}
                            </Typography>
                            <Typography
                              component="span"
                              sx={{
                                color:
                                  maxEnergySavings < 7.5
                                    ? 'error.main'
                                    : maxEnergySavings <= 18
                                      ? 'warning.main'
                                      : 'success.main',
                              }}
                            >
                              {/* {maxEnergySavings.toFixed(1)}% */}
                              {maxEnergySavings.toFixed(1)}%
                            </Typography>
                          </>
                        )}
                      </>
                    }
                    slotProps={{
                      primary: {
                        fontSize: '0.8rem',
                        color: theme.palette.text.secondary,
                      },

                      secondary: {
                        component: 'span',
                        color: theme.palette.text.primary,
                      }
                    }} />
                ) : (
                  <ListItemText
                    primary="N/A"
                    secondary="No data"
                    slotProps={{
                      primary: {
                        fontSize: '0.8rem',
                        color: theme.palette.text.secondary,
                      },

                      secondary: {
                        component: 'span',
                        color: theme.palette.text.primary,
                      }
                    }} />
                )}
              </Box>
            </Box>

            <Divider sx={{ mt: 1, mb: 1 }} />

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">
                  {t('translation:energy-data-upload')}
                </Typography>
                <HtmlTooltip
                  title={t('buildingOptimization:tooltips.billingMeterLatency')}
                  content={sortedBuildings.map((building, index) => {
                    const latency = getMaxLatencyHours(building);
                    const { critical_errors } = getBillingMeterHealthStatus(
                      building.State,
                    );
                    return (
                      <ListItemText
                        key={index}
                        primary={
                          <div
                            style={{
                              display: 'flex',
                              flexDirecion: 'column',
                              alignItems: 'center',
                            }}
                          >
                            <Chip
                              icon={
                                critical_errors?.length > 0 || !latency ? (
                                  <ErrorRoundedIcon
                                    fontSize="small"
                                    color="error"
                                  />
                                ) : (
                                  <ApartmentRoundedIcon />
                                )
                              }
                              size="small"
                              sx={{ borderRadius: 1, mr: 1 }}
                              label={<>{building.Name}</>}
                            />
                          </div>
                        }
                        secondary={
                          critical_errors?.length > 0 ? (
                            critical_errors?.map((error, i) => (
                              <Box
                                key={i}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mt: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {parseAndFormatErrorMessage(error.message)}
                                </Typography>
                              </Box>
                            ))
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                color: getLatencyColor(latency),
                                mt: 1,
                              }}
                            >
                              {latency !== null
                                ? `${Math.round(latency)} hours`
                                : 'N/A'}
                            </Typography>
                          )
                        }
                      />
                    );
                  })}
                >
                  {criticalErrors?.filter((error) => error !== undefined)
                    .length > 0 ||
                  (minLatencyHours === Infinity &&
                    maxLatencyHours === -Infinity) ||
                  sortedBuildings.some(
                    (building) => getMaxLatencyHours(building) === null,
                  ) ? (
                    <ErrorRoundedIcon
                      fontSize="small"
                      color="error"
                      sx={{ ml: 0.5 }}
                    />
                  ) : (
                    <InfoRoundedIcon
                      fontSize="small"
                      sx={{ ml: 0.5, color: 'divider' }}
                    />
                  )}
                </HtmlTooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'end' }}>
                {minLatencyHours !== Infinity &&
                maxLatencyHours !== -Infinity ? (
                  <Typography variant="body1">
                    <Typography
                      component="span"
                      sx={{
                        color: getLatencyColor(minLatencyHours),
                      }}
                    >
                      {Math.round(minLatencyHours)}{' '}
                      {t('units.hour.indefinitePlural', { ns: 'time' })}
                    </Typography>
                    {minLatencyHours !== maxLatencyHours && (
                      <>
                        <Typography
                          component="span"
                          sx={{ color: 'text.primary' }}
                        >
                          {' '}
                          -{' '}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            color: getLatencyColor(maxLatencyHours),
                          }}
                        >
                          {Math.round(maxLatencyHours)}{' '}
                          {t('units.hour.indefinitePlural', { ns: 'time' })}
                        </Typography>
                      </>
                    )}
                  </Typography>
                ) : (
                  <Typography variant="body1" style={{ cursor: 'pointer' }}>
                    <Typography
                      component="span"
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                    >
                      N/A
                    </Typography>
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'end' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2">
                    {t('operations.higherConsumptionHours', {
                      ns: 'buildingOptimization',
                    })}
                  </Typography>
                </Box>
                <HtmlTooltip
                  title={t('operations.higherConsumptionHours', {
                    ns: 'buildingOptimization',
                  })}
                  content={sortedBuildings.map((building, index) => {
                    const latency = getMaxLatencyConsumptionHours(building);

                    return (
                      <ListItemText
                        key={index}
                        primary={
                          <div
                            style={{
                              display: 'flex',
                              flexDirecion: 'column',
                              alignItems: 'center',
                            }}
                          >
                            <Chip
                              icon={
                                !latency ? (
                                  <ErrorRoundedIcon
                                    fontSize="small"
                                    color="error"
                                  />
                                ) : (
                                  <ApartmentRoundedIcon />
                                )
                              }
                              size="small"
                              sx={{ borderRadius: 1, mr: 1 }}
                              label={<>{building.Name}</>}
                            />
                          </div>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            sx={{
                              color: getLatencyColor(latency),
                              mt: 1,
                            }}
                          >
                            {latency !== null
                              ? `${Math.round(latency)} ${t('units.hour.plural', { ns: 'time' })}`
                              : 'N/A'}
                          </Typography>
                        }
                      />
                    );
                  })}
                >
                  {(minLatencyConsumptionHours === Infinity &&
                    maxLatencyConsumptionHours === -Infinity) ||
                  sortedBuildings.some(
                    (building) =>
                      getMaxLatencyConsumptionHours(building) === null,
                  ) ? (
                    <ErrorRoundedIcon
                      fontSize="small"
                      color="error"
                      sx={{ ml: 0.5 }}
                    />
                  ) : (
                    <InfoRoundedIcon
                      fontSize="small"
                      sx={{ ml: 0.5, color: 'divider' }}
                    />
                  )}
                </HtmlTooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'end' }}>
                {minLatencyConsumptionHours !== Infinity &&
                maxLatencyConsumptionHours !== -Infinity ? (
                  <Typography variant="body1">
                    <Typography
                      component="span"
                      sx={{
                        color: getLatencyColor(minLatencyConsumptionHours),
                      }}
                    >
                      {Math.round(minLatencyConsumptionHours)}{' '}
                      {t('units.hour.indefinitePlural', { ns: 'time' })}
                    </Typography>
                    {minLatencyConsumptionHours !==
                      maxLatencyConsumptionHours && (
                      <>
                        <Typography
                          component="span"
                          sx={{ color: 'text.primary' }}
                        >
                          {' '}
                          -{' '}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            color: getLatencyColor(maxLatencyConsumptionHours),
                          }}
                        >
                          {Math.round(maxLatencyConsumptionHours)}{' '}
                          {t('units.hour.indefinitePlural', { ns: 'time' })}
                        </Typography>
                      </>
                    )}
                  </Typography>
                ) : (
                  <Typography variant="body1" style={{ cursor: 'pointer' }}>
                    <Typography
                      component="span"
                      sx={{
                        color: theme.palette.text.secondary,
                      }}
                    >
                      N/A
                    </Typography>
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">QoS</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'end' }}>
                <Typography variant="body1" style={{ cursor: 'pointer' }}>
                  <Typography component="span">{minQos.toFixed(1)}</Typography>
                  {minQos !== maxQos && (
                    <>
                      <Typography
                        component="span"
                        sx={{ color: 'text.primary' }}
                      >
                        {' '}
                        /{' '}
                      </Typography>
                      <Typography component="span">
                        {maxQos.toFixed(1)}
                      </Typography>
                    </>
                  )}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">
                  {t('translation:uptime')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex' }}>
                <HtmlTooltip
                  title={t('buildingOptimization:tooltips.gatewayUptimes')}
                  content={sortedGatewayHealthStatuses.map((status, index) => (
                    <ListItemText
                      key={index}
                      primary={
                        <div style={{ padding: '5px' }}>
                          <Chip
                            icon={<RouterRoundedIcon />}
                            size="small"
                            sx={{ borderRadius: 1, mr: 1 }}
                            label={status.gatewayId}
                          />
                          {status.displayName}{' '}
                        </div>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LinearProgress
                            variant="determinate"
                            value={status.uptimePercentage}
                            sx={{
                              flex: 1,
                              height: 10,
                              minWidth: 100,
                              borderRadius: 5,
                              mr: 1, // Adds space between progress bar and percentage
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getProgressColor(
                                  status.uptimePercentage,
                                ),
                              },
                            }}
                          />
                          <Typography fontSize="0.8rem" color="text.secondary">
                            {status.uptimePercentage.toFixed(0)}%
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                      cursor: 'pointer',
                      minWidth: 150,
                    }}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={minUptimePercentage}
                      sx={{
                        flex: 1,
                        height: 10,
                        borderRadius: 5,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor:
                            getProgressColor(minUptimePercentage),
                        },
                      }}
                    />
                    <Typography
                      fontSize="0.8rem"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      {minUptimePercentage.toFixed(0)}%
                    </Typography>
                  </Box>
                </HtmlTooltip>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
};

export default OperationsCard;
